/**
 * useLiveSync — keeps every device showing the latest shared data.
 *
 * Because each store now reads/writes the SERVER copy, we just need to re-pull
 * it periodically so changes made by another user (on another device/browser)
 * appear here. We re-hydrate every store on tab focus, when the tab becomes
 * visible again, and on a gentle interval. Auth is intentionally NOT synced —
 * each device stays logged in as its own user.
 *
 * Note: a hard re-pull can replace what you're looking at, so save an in-progress
 * edit before switching away. True instant push (WebSockets) is a later phase.
 */
import { useEffect } from 'react'
import { useForecastStore } from '../store/forecastStore'
import { usePurchaseStore } from '../store/purchaseStore'
import { useInventoryStore } from '../store/inventoryStore'
import { useProductionStore } from '../store/productionStore'
import { useLeadStore, useSalesOrderStore } from '../store/salesStore'
import { useQCStore } from '../store/qcStore'
import { useInvoiceStore } from '../store/financeStore'
import { useHRStore } from '../store/hrStore'
import { useSettingsStore, useUsersStore } from '../store/settingsStore'
import { useSalesBookStore } from '../store/salesBookStore'
import { usePriceBookStore } from '../store/priceBookStore'
import { useDiamondPurchaseStore } from '../store/diamondPurchaseStore'

type Rehydratable = { persist?: { rehydrate?: () => void | Promise<void> } }

const SYNCED: Rehydratable[] = [
  useForecastStore, usePurchaseStore, useInventoryStore, useProductionStore,
  useLeadStore, useSalesOrderStore, useQCStore, useInvoiceStore, useHRStore,
  useSettingsStore, useUsersStore, useSalesBookStore, usePriceBookStore, useDiamondPurchaseStore,
]

export function useLiveSync() {
  useEffect(() => {
    const sync = () => {
      if (document.hidden) return
      SYNCED.forEach((s) => { try { s.persist?.rehydrate?.() } catch { /* ignore */ } })
    }
    window.addEventListener('focus', sync)
    document.addEventListener('visibilitychange', sync)
    const id = window.setInterval(sync, 10000)
    return () => {
      window.removeEventListener('focus', sync)
      document.removeEventListener('visibilitychange', sync)
      window.clearInterval(id)
    }
  }, [])
}
